import { useState, useEffect, useCallback, useMemo } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { credentialRequestsApi, getErrorMessage } from '../services/api';
import { Button } from '../components/ui/button';
import ListPagination, { LIST_PAGE_SIZE } from '../components/ui/ListPagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { RefreshCw, Clock, Eye, CheckCircle2, Ban, AlertTriangle, Mail, X, Shield, AlertCircle, ChevronDown, ChevronUp, FileText, Upload, XCircle } from 'lucide-react';
import { format } from 'date-fns';

// Mirrors TrackingPage.js's StatusBadge (documents) with credential
// requests' own status set — "opened"/"in_progress" both show as an active
// mid-flight state, "email_failed" surfaces the one send-time failure mode
// documents don't have.
const StatusBadge = ({ status }) => {
  const config = {
    sent: { class: 'bg-blue-100 text-blue-700', label: 'Sent', icon: Clock },
    opened: { class: 'bg-amber-100 text-amber-700', label: 'Opened', icon: Eye },
    in_progress: { class: 'bg-amber-100 text-amber-700', label: 'In Progress', icon: RefreshCw },
    completed: { class: 'bg-emerald-100 text-emerald-700', label: 'Completed', icon: CheckCircle2 },
    cancelled: { class: 'bg-gray-100 text-gray-600', label: 'Cancelled', icon: Ban },
    email_failed: { class: 'bg-red-100 text-red-700', label: 'Email Failed', icon: AlertTriangle },
  };
  const { class: className, label, icon: Icon } = config[status] || config.sent;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${className}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

const PENDING_STATUSES = ['sent', 'opened', 'in_progress'];

// Per-item status, distinct from the request-level StatusBadge above —
// an item is "pending" (nothing uploaded yet), "uploaded" (awaiting the
// Bullhorn write-back), "verified" (written back successfully — this is
// what counts as "completed"), or "failed" (write-back errored).
const ItemStatusBadge = ({ status }) => {
  const config = {
    pending: { class: 'bg-slate-100 text-slate-600', label: 'Not uploaded', icon: Clock },
    uploaded: { class: 'bg-blue-100 text-blue-700', label: 'Uploaded', icon: Upload },
    verified: { class: 'bg-emerald-100 text-emerald-700', label: 'Verified', icon: CheckCircle2 },
    failed: { class: 'bg-red-100 text-red-700', label: 'Failed', icon: XCircle },
    cancelled: { class: 'bg-gray-100 text-gray-600', label: 'Cancelled', icon: Ban },
  };
  const { class: className, label, icon: Icon } = config[status] || config.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${className}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

// Mirrors RecipientsPage.js's ProgressIndicator so Credential Requests
// shows completed-vs-outstanding the same way the By Recipient tab does.
const ProgressIndicator = ({ completed, total }) => {
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  let colorClass = 'bg-red-500';
  if (percentage >= 100) colorClass = 'bg-emerald-500';
  else if (percentage >= 50) colorClass = 'bg-amber-500';
  else if (percentage > 0) colorClass = 'bg-orange-500';

  return (
    <div className="flex items-center gap-3">
      <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${colorClass}`} style={{ width: `${percentage}%` }} />
      </div>
      <span className="text-sm font-medium min-w-[40px]">{completed}/{total}</span>
    </div>
  );
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return format(new Date(value), 'd MMM yyyy, HH:mm');
  } catch {
    return value;
  }
};

// Outstanding / Completed two-column item list for one request — mirrors
// RecipientsPage.js's RecipientDocumentsPanel layout.
// `cancelled`: the whole request was cancelled, so anything not yet verified
// isn't outstanding any more — it's listed as Cancelled instead.
const CredentialItemsList = ({ items, cancelled = false }) => {
  const outstandingItems = items?.filter((it) => it.status !== 'verified') || [];
  const completedItems = items?.filter((it) => it.status === 'verified') || [];
  const tone = cancelled
    ? { border: 'border-gray-200', icon: 'text-gray-500', heading: 'text-gray-600', title: 'Cancelled', empty: 'Nothing cancelled' }
    : { border: 'border-amber-200', icon: 'text-amber-600', heading: 'text-amber-900', title: 'Outstanding', empty: 'No outstanding credentials' };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        <div className="flex items-center gap-2 mb-3">
          {cancelled ? <Ban className="w-4 h-4 text-gray-500" /> : <AlertCircle className="w-4 h-4 text-amber-600" />}
          <h4 className={`font-semibold text-sm ${tone.heading}`}>{tone.title} ({outstandingItems.length})</h4>
        </div>
        {outstandingItems.length > 0 ? (
          <div className="space-y-2">
            {outstandingItems.map((item) => (
              <div key={item.id} className={`flex items-center justify-between p-3 bg-white rounded-lg border ${tone.border} shadow-sm`}>
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className={`w-4 h-4 ${tone.icon} flex-shrink-0`} />
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{item.bullhorn_category_name}</p>
                    {item.required && <p className="text-xs text-muted-foreground">Required</p>}
                    {!cancelled && item.status === 'failed' && item.bullhorn_error && (
                      <p className="text-xs text-red-600 truncate" title={item.bullhorn_error}>{item.bullhorn_error}</p>
                    )}
                  </div>
                </div>
                <ItemStatusBadge status={cancelled ? 'cancelled' : item.status} />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground italic p-3 bg-white rounded-lg border border-dashed">{tone.empty}</p>
        )}
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <h4 className="font-semibold text-emerald-900 text-sm">Completed ({completedItems.length})</h4>
        </div>
        {completedItems.length > 0 ? (
          <div className="space-y-2">
            {completedItems.map((item) => (
              <div key={item.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-emerald-200 shadow-sm">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{item.bullhorn_category_name}</p>
                    {item.file_name && <p className="text-xs text-muted-foreground truncate">{item.file_name}</p>}
                  </div>
                </div>
                <ItemStatusBadge status={item.status} />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground italic p-3 bg-white rounded-lg border border-dashed">No completed credentials yet</p>
        )}
      </div>
    </div>
  );
};

// Expanded detail panel for one candidate — a candidate can have several
// separate credential-request sends over time (re-requested categories,
// renewals, etc.), each with its own status/link/remind-cancel actions, so
// this lists every one of that candidate's requests (newest first) with its
// own item breakdown, rather than flattening them into a single list that
// would lose which items belong to which still-open (or already cancelled)
// request.
const CandidateRequestsPanel = ({ requests, onRemind, onCancel, busyId }) => (
  <div className="bg-slate-50 border-t border-border p-6 space-y-4 animate-in slide-in-from-top-2 duration-200">
    {requests.map((request) => {
      const isPending = PENDING_STATUSES.includes(request.status);
      const isBusy = busyId === request.id;
      return (
        <div key={request.id} className="bg-white rounded-lg border border-border overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-border">
            <div className="flex items-center gap-3">
              <StatusBadge status={request.status} />
              <span className="text-xs text-muted-foreground">Sent {formatDate(request.sent_at)}</span>
            </div>
            {isPending && (
              <div className="flex gap-2">
                <Button
                  size="sm" variant="outline" className="h-7 text-xs"
                  onClick={() => onRemind(request)}
                  disabled={isBusy}
                  data-testid={`remind-credential-${request.id}`}
                >
                  <Mail className="w-3 h-3 mr-1" />
                  Remind
                </Button>
                <Button
                  size="sm" variant="outline" className="h-7 text-xs text-red-600 hover:text-red-700"
                  onClick={() => onCancel(request)}
                  disabled={isBusy}
                  data-testid={`cancel-credential-${request.id}`}
                >
                  <X className="w-3 h-3 mr-1" />
                  Cancel
                </Button>
              </div>
            )}
          </div>
          <div className="p-4">
            <CredentialItemsList items={request.items} cancelled={request.status === 'cancelled'} />
          </div>
        </div>
      );
    })}
  </div>
);

export default function CredentialRequestsPage({ embedded = false }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [busyId, setBusyId] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [page, setPage] = useState(1);

  const loadRequests = useCallback(async (status) => {
    setLoading(true);
    try {
      const response = await credentialRequestsApi.list(status === 'all' ? undefined : status);
      setRequests(response.data?.requests || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load credential requests'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRequests(statusFilter);
    setPage(1);
  }, [statusFilter, loadRequests]);

  const handleCancel = async (request) => {
    if (!window.confirm(`Cancel this credential request? ${request.candidate_name}'s link will stop working.`)) return;
    setBusyId(request.id);
    try {
      await credentialRequestsApi.cancel(request.id);
      toast.success('Request cancelled');
      loadRequests(statusFilter);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to cancel request'));
    } finally {
      setBusyId(null);
    }
  };

  const handleRemind = async (request) => {
    setBusyId(request.id);
    try {
      await credentialRequestsApi.remind(request.id);
      toast.success('Reminder sent');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send reminder'));
    } finally {
      setBusyId(null);
    }
  };

  // Top-level row action — nudges the candidate about every one of their
  // still-open requests in one click, rather than making the recruiter
  // expand the row and remind each one individually.
  const handleRemindGroup = async (group) => {
    const pending = group.requests.filter((r) => PENDING_STATUSES.includes(r.status));
    setBusyId(group.candidate_email);
    try {
      // One call, one email — each credential is its own request, so reminding
      // them individually would send the candidate a separate email for each.
      const { data } = await credentialRequestsApi.remindMany(pending.map((r) => r.id));
      if (!data?.candidates_notified) {
        toast.error(data?.skipped_not_open ? 'Those requests are no longer open.' : 'The reminder email could not be sent.');
        loadRequests(statusFilter);
      } else {
        toast.success(`Reminder sent (${group.outstanding} credential${group.outstanding === 1 ? '' : 's'} outstanding)`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send reminder'));
    } finally {
      setBusyId(null);
    }
  };

  // "verified" = written back to Bullhorn successfully; everything else
  // (pending/uploaded/failed) is still outstanding from the recruiter's
  // point of view — matches RecipientsPage.js's outstanding/completed split.
  // A cancelled request has nothing left to do: whatever was already verified
  // before it was cancelled still counts as completed, but the rest is no
  // longer outstanding (and drops out of the total, so progress isn't dragged
  // down by credentials that will never be uploaded).
  const requestCounts = (request) => {
    const items = request.items || [];
    const completed = items.filter((it) => it.status === 'verified').length;
    const outstanding = request.status === 'cancelled' ? 0 : items.length - completed;
    return { total: completed + outstanding, completed, outstanding };
  };

  // A candidate can have multiple separate credential requests sent over
  // time (each its own row from the API) — group them by candidate so the
  // table shows one row per candidate, the same way By Recipient groups
  // documents. Expanding a row reveals every one of that candidate's
  // individual requests (see CandidateRequestsPanel).
  const candidateGroups = useMemo(() => {
    const byEmail = new Map();
    for (const request of requests) {
      const key = request.candidate_email;
      if (!byEmail.has(key)) byEmail.set(key, []);
      byEmail.get(key).push(request);
    }
    return Array.from(byEmail.entries()).map(([email, reqs]) => {
      const sorted = [...reqs].sort((a, b) => (b.sent_at || '').localeCompare(a.sent_at || ''));
      const counts = reqs.map(requestCounts);
      const total = counts.reduce((sum, n) => sum + n.total, 0);
      const completed = counts.reduce((sum, n) => sum + n.completed, 0);
      const outstanding = counts.reduce((sum, n) => sum + n.outstanding, 0);
      return {
        candidate_email: email,
        candidate_name: sorted[0]?.candidate_name || email,
        requests: sorted,
        requestsSent: reqs.length,
        lastSentAt: sorted[0]?.sent_at,
        total,
        completed,
        outstanding,
        hasPending: reqs.some((r) => PENDING_STATUSES.includes(r.status)),
      };
    }).sort((a, b) => (b.lastSentAt || '').localeCompare(a.lastSentAt || ''));
  }, [requests]);

  // Pages of candidates (one row per candidate), 25 at a time like the other Documents tabs.
  const totalPages = Math.max(1, Math.ceil(candidateGroups.length / LIST_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedGroups = candidateGroups.slice((currentPage - 1) * LIST_PAGE_SIZE, currentPage * LIST_PAGE_SIZE);

  const content = (
    <div className="space-y-6" data-testid="credential-requests-page">
      {!embedded && (
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Credential Requests</h1>
          <p className="text-muted-foreground font-body mt-1">
            Track and manage credential requests sent to candidates
          </p>
        </div>
      )}

      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="credential-status-filter">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="opened">Opened</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="email_failed">Email Failed</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => loadRequests(statusFilter)} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="neumorphic rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-16">
            <Shield className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No credential requests found</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Candidate</TableHead>
                <TableHead>Requests Sent</TableHead>
                <TableHead>Outstanding</TableHead>
                <TableHead>Completed</TableHead>
                <TableHead>Last Activity</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagedGroups.map((group) => {
                const isBusy = busyId === group.candidate_email;
                const isExpanded = expandedRow === group.candidate_email;
                return (
                  <>
                    <TableRow
                      key={group.candidate_email}
                      className={`cursor-pointer transition-colors ${isExpanded ? 'bg-slate-50' : 'hover:bg-slate-50/50'}`}
                      onClick={() => setExpandedRow(isExpanded ? null : group.candidate_email)}
                      data-testid={`credential-candidate-row-${group.candidate_email}`}
                    >
                      <TableCell className="text-center">
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-muted-foreground mx-auto" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-muted-foreground mx-auto" />
                        )}
                      </TableCell>
                      <TableCell>
                        <p className="font-medium text-sm">{group.candidate_name}</p>
                        <p className="text-xs text-muted-foreground">{group.candidate_email}</p>
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold text-lg">{group.requestsSent}</span>
                      </TableCell>
                      <TableCell>
                        {group.outstanding > 0 ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                            <AlertCircle className="w-3.5 h-3.5" />
                            {group.outstanding}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            None
                          </span>
                        )}
                      </TableCell>
                      <TableCell><ProgressIndicator completed={group.completed} total={group.total} /></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatDate(group.lastSentAt)}</TableCell>
                      <TableCell className="text-right">
                        {group.hasPending && (
                          <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm" variant="outline" className="h-7 text-xs"
                              onClick={() => handleRemindGroup(group)}
                              disabled={isBusy}
                              data-testid={`remind-candidate-${group.candidate_email}`}
                            >
                              <Mail className="w-3 h-3 mr-1" />
                              Remind
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow key={`${group.candidate_email}-details`}>
                        <TableCell colSpan={7} className="p-0">
                          <CandidateRequestsPanel
                            requests={group.requests}
                            onRemind={handleRemind}
                            onCancel={handleCancel}
                            busyId={busyId}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <ListPagination
        page={currentPage}
        totalPages={totalPages}
        totalCount={candidateGroups.length}
        noun="candidates"
        onPageChange={(p) => { setPage(p); setExpandedRow(null); }}
      />
    </div>
  );

  if (embedded) return content;

  return <DashboardLayout>{content}</DashboardLayout>;
}
